// =============================================================================
// staleness.ts — "Needs review" detection for scholarships (v0.2.38)
// =============================================================================
//
// Detects scholarships whose tracked information may be out of date, so
// nothing rots silently in the list. Computed client-side from data the app
// already loads — the same pattern as the v0.2.31 computed badges. No Cloud
// Function, no schema change, no background job: it recalculates every render
// and is therefore always current.
//
// Three honest, detectable conditions (see STALE_REASON_LABELS). A scholarship
// is only flagged when a condition is DEFINITELY true — missing/unknown data
// never produces a flag.
// =============================================================================

import type { Scholarship } from '@scholarship-app/types';
import { normalizeToIso } from './dates';

export type StaleReason =
  | 'passed_deadline'   // deadline is in the past, not resolved
  | 'untouched'         // active but not edited in a long time
  | 'unverified_url';   // url never confirmed, and entry isn't new

export const STALE_REASON_LABELS: Record<StaleReason, string> = {
  passed_deadline: 'Deadline has passed but it isn\u2019t marked submitted or closed',
  untouched: 'No updates in a while \u2014 check it\u2019s still current',
  unverified_url: 'Link was never verified',
};

/** Tunable thresholds (days). */
const UNTOUCHED_DAYS = 60;
const UNVERIFIED_MIN_AGE_DAYS = 30;

/** Statuses that mean the scholarship is "done" — never stale. */
const RESOLVED_STATUSES = new Set(['submitted', 'awarded', 'rejected', 'expired']);
/** Statuses that count as "active" for the untouched check. */
const ACTIVE_STATUSES = new Set(['identified', 'researching', 'in_progress']);

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Coerce a timestamp field to a Date. Firestore fields written with
 * serverTimestamp() come back as Timestamp objects (with .toDate()), NOT
 * strings — even though the type declares them as `string`. Handle both, plus
 * Date objects and millisecond numbers, and return null for anything else.
 */
function toDate(value: unknown): Date | null {
  if (!value) return null;
  // Firestore Timestamp — has a toDate() method.
  if (typeof value === 'object' && value !== null &&
      'toDate' in value && typeof (value as { toDate: unknown }).toDate === 'function') {
    const d = (value as { toDate: () => Date }).toDate();
    return isNaN(d.getTime()) ? null : d;
  }
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === 'number') {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === 'string') {
    const iso = normalizeToIso(value) ?? value.slice(0, 10);
    const d = new Date(iso + 'T00:00:00');
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}

/** Whole days between a timestamp value and today (negative if future). */
function daysSince(value: unknown): number | null {
  const then = toDate(value);
  if (!then) return null;
  const now = new Date(todayIso() + 'T00:00:00');
  return Math.floor((now.getTime() - then.getTime()) / 86_400_000);
}

/**
 * The staleness reasons that apply to a scholarship. Empty array = not stale.
 * Archived and resolved (submitted/awarded/rejected/expired) scholarships are
 * never flagged.
 */
export function stalenessReasons(s: Scholarship): StaleReason[] {
  if (s.archived) return [];
  if (RESOLVED_STATUSES.has(s.status)) return [];

  const reasons: StaleReason[] = [];

  // 1. Passed deadline, not resolved. Rolling-deadline scholarships are
  //    excluded — "rolling" means there isn't a fixed date to pass.
  if (!s.deadline_rolling) {
    const deadline = normalizeToIso(s.effective_deadline ?? s.deadline);
    if (deadline && deadline < todayIso()) {
      reasons.push('passed_deadline');
    }
  }

  // 2. Active but untouched for a long time, AND still has a future deadline
  //    (a passed deadline is already caught by #1; don't double-flag).
  if (ACTIVE_STATUSES.has(s.status)) {
    const idle = daysSince(s.updated_at);
    const deadline = normalizeToIso(s.effective_deadline ?? s.deadline);
    const deadlineFuture = !deadline || deadline >= todayIso();
    if (idle != null && idle >= UNTOUCHED_DAYS && deadlineFuture) {
      reasons.push('untouched');
    }
  }

  // 3. URL never verified, and the entry is not brand new.
  if (!s.url_verified && s.url) {
    const age = daysSince(s.created_at);
    if (age != null && age >= UNVERIFIED_MIN_AGE_DAYS) {
      reasons.push('unverified_url');
    }
  }

  return reasons;
}

/** Convenience: is this scholarship stale at all? */
export function isStale(s: Scholarship): boolean {
  return stalenessReasons(s).length > 0;
}

/** Count of stale scholarships in a list (for the Dashboard summary). */
export function countStale(scholarships: Scholarship[]): number {
  return scholarships.reduce((n, s) => (isStale(s) ? n + 1 : n), 0);
}
